import { Text } from "@certn/cascadia-react";

import { InvoiceProps, type Taxes } from "../../../../src/schemas-and-types";

export function isNotZero(value: string): boolean {
  const floatValue = parseFloat(value);
  return floatValue !== 0;
}

/**
 * Format a stored tax rate (e.g. "0.20", "0.09975") as a percent label.
 * Do not Math.round: QST is 9.975% and rounding would print 10%.
 */
export function formatTaxRatePercent(rate: string): string {
  const percent = Number(rate) * 100;
  if (!Number.isFinite(percent)) {
    return "";
  }
  const text = percent.toFixed(5).replace(/\.?0+$/, "");
  return `${text}%`;
}

export function formatTaxLabel(name: string, rate: string): string {
  const percent = formatTaxRatePercent(rate);
  return percent ? `${name} (${percent})` : name;
}

export function buildTaxRows(
  taxes: Taxes,
  currencySymbol: string,
  segmentKey: string,
  labelKey: "tax" | "itemPrice"
) {
  return Object.entries(taxes)
    .filter(([, tax]) => isNotZero(tax.amount))
    .map(([taxKey, tax]) => ({
      key: `${segmentKey}:summary:${taxKey}`,
      [labelKey]: (
        <Text style="global/button-medium">
          {formatTaxLabel(tax.name, tax.rate)}
        </Text>
      ),
      lineAmount: (
        <Text style="global/button-medium">
          {currencySymbol}
          {numberFormatter(tax.amount)}
        </Text>
      ),
    }));
}

export function segmentHasNonZeroLineTax(
  segment: InvoiceProps["invoiceData"]["segments"][string]
): boolean {
  return Object.values(segment.line_items).some(
    (item) => item.tax_amount != null && isNotZero(item.tax_amount)
  );
}

export function numberFormatter(value: string): string {
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(parseFloat(value));
}

function isFutureDate(collectAtDate: string): boolean {
  const now = new Date();
  const dateObj = new Date(collectAtDate);
  return dateObj.getTime() > now.getTime();
}

/**
 * Determines whether to show an auto-collect alert based on the given invoice data.
 *
 * This function evaluates the conditions for displaying an alert to indicate
 * that an invoice will be automatically collected using a stored payment method.
 * The conditions are as follows:
 *
 * - `collect_at` must be defined and set to a future date.
 * - `billing_profile.payment_profile` must be present (indicating a payment profile is linked).
 * - `billing_profile.collection_method` must be set to "AUTOMATIC".
 * - `billing_profile.payment_method` must be "CREDIT_CARD".
 * - `billing_profile.invoicing_frequency` must NOT be "DAILY".
 *
 * If all these conditions are met, the function returns `true`, otherwise it returns `false`.
 *
 * @param {InvoiceProps["invoiceData"]} invoiceData - The invoice data containing billing
 * profile and collection details.
 * @returns {boolean} - `true` if the auto-collect alert should be shown, `false` otherwise.
 */
export function getShowAutoCollectAlert(
  invoiceData: InvoiceProps["invoiceData"]
) {
  const { billing_profile, collect_at } = invoiceData;
  const {
    collection_method,
    payment_method,
    payment_profile,
    invoicing_frequency,
  } = billing_profile;

  if (!collect_at) return false;
  if (!payment_profile) return false;
  if (collect_at && !isFutureDate(collect_at)) return false;
  if (invoicing_frequency === "DAILY") return false;

  return collection_method === "AUTOMATIC" && payment_method === "CREDIT_CARD";
}

export function getCurrencySymbol(currency: string): string {
  switch (currency) {
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    default:
      return "$";
  }
}

/** Invoices issued on or after this UTC date use the Melbourne office. */
const INTERCHECK_MELBOURNE_OFFICE_ISSUE_DATE = "2026-10-01";

function calendarIssueDate(issueDate: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(issueDate);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return `${match[1]}-${match[2]}-${match[3]}`;
}

function usesMelbourneOfficeAddress(
  issueDate: string | null | undefined
): boolean {
  if (!issueDate) {
    return false;
  }
  const date = calendarIssueDate(issueDate);
  return date !== null && date >= INTERCHECK_MELBOURNE_OFFICE_ISSUE_DATE;
}

export function getCertnAddress(subsidiary: string, issueDate?: string | null) {
  if (subsidiary === "CERTN_USA") {
    return (
      <Text style="global/info-label">
        Certn (USA) Inc.
        <br />
        1209 Orange Street
        <br />
        Wilmington, DE 19801, US
      </Text>
    );
  } else if (subsidiary === "CREDENCE") {
    return (
      <Text style="global/info-label">
        Certn (UK) Ltd
        <br />
        VAT: 428125017
        <br />
        Company Reg: 2998939
        <br />
        5 New Street Square
        <br />
        London, EC4A 3TW
      </Text>
    );
  } else if (subsidiary === "INTERCHECK") {
    if (usesMelbourneOfficeAddress(issueDate)) {
      return (
        <Text style="global/info-label">
          InterCheck Global Pty Ltd d/b/a Certn
          <br />
          ABN#: 51 151 778237
          <br />
          Ofc 7140 - Level 7
          <br />
          180 Flinders Street
          <br />
          Melbourne, VIC 3000
        </Text>
      );
    }
    return (
      <Text style="global/info-label">
        InterCheck Global Pty Ltd
        <br />
        ABN#: 51 151 778237
        <br />
        PO Box 213, Flinders Lane
        <br />
        VIC 8009, Australia
      </Text>
    );
  } else {
    return (
      <Text style="global/info-label">
        Certn (Canada) Inc.
        <br />
        GST#: 705986727RT0001
        <br />
        Unit 300 - 1006 Fort St
        <br />
        Victoria, BC, V8V 3K4, Canada
      </Text>
    );
  }
}
